import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MessageCircle, Mail, Send, Loader2, Newspaper } from "lucide-react";
import { Reveal, Eyebrow, SectionTitle } from "./Reveal";
import { API, SERVICES, WA_DEFAULT } from "@/data/content";

const inputCls =
  "w-full rounded-lg border border-line bg-surface px-4 py-3 text-sm text-ink placeholder:text-dim outline-none transition-colors duration-200 focus:border-brand";

export default function ContactSection() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: SERVICES[0].title, message: "" });
  const [sending, setSending] = useState(false);
  const [newsEmail, setNewsEmail] = useState("");
  const [subscribing, setSubscribing] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submitContact = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Consultation request received. Our desk will respond shortly.");
      setForm({ name: "", email: "", phone: "", service: SERVICES[0].title, message: "" });
    } catch {
      toast.error("Could not send your request. Please try WhatsApp instead.");
    } finally {
      setSending(false);
    }
  };

  const submitNewsletter = async (e) => {
    e.preventDefault();
    setSubscribing(true);
    try {
      const { data } = await axios.post(`${API}/newsletter`, { email: newsEmail });
      toast.success(data.existing ? "You are already on the dispatch list." : "Subscribed. Welcome to the Weekly Alpha dispatch.");
      setNewsEmail("");
    } catch {
      toast.error("Subscription failed. Please try again.");
    } finally {
      setSubscribing(false);
    }
  };

  return (
    <section id="advisory" data-testid="contact-section" className="mx-auto max-w-7xl px-4 py-24 sm:px-6">
      <Reveal>
        <div className="mb-12">
          <Eyebrow>Executive Contact</Eyebrow>
          <SectionTitle className="mt-3">
            Open a Line to <span className="text-brand">the Desk</span>
          </SectionTitle>
        </div>
      </Reveal>

      <div className="grid gap-4 lg:grid-cols-5">
        <div className="flex flex-col gap-4 lg:col-span-2">
          <Reveal>
            <a
              data-testid="contact-whatsapp-button"
              href={WA_DEFAULT}
              target="_blank"
              rel="noopener noreferrer"
              className="card-trace group flex items-center gap-4 rounded-xl border border-wa/40 bg-panel p-6"
            >
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-wa text-white">
                <MessageCircle size={20} />
              </span>
              <div>
                <p className="font-display text-xl font-bold uppercase tracking-wide text-ink">
                  WhatsApp Direct
                </p>
                <p className="font-mono text-[11px] tracking-[0.1em] text-mute">
                  +1 260 568 7097 — fastest response
                </p>
              </div>
            </a>
          </Reveal>
          <Reveal delay={0.08}>
            <a
              data-testid="contact-email-support"
              href="mailto:support@teamx.education?subject=TEAM%20X%20Support%20Request"
              className="card-trace flex items-center gap-4 rounded-xl border border-line bg-panel p-6"
            >
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-brand/40 bg-brand/10 text-brand">
                <Mail size={20} />
              </span>
              <div>
                <p className="font-display text-xl font-bold uppercase tracking-wide text-ink">
                  Email Support
                </p>
                <p className="font-mono text-[11px] tracking-[0.1em] text-mute">
                  support@teamx.education
                </p>
              </div>
            </a>
          </Reveal>
          <Reveal delay={0.16} className="flex-1">
            <form
              onSubmit={submitNewsletter}
              className="card-trace flex h-full flex-col justify-between rounded-xl border border-line bg-panel p-6"
            >
              <div className="flex items-center gap-3">
                <Newspaper size={18} className="text-brand" />
                <div>
                  <p className="font-display text-xl font-bold uppercase tracking-wide text-ink">
                    Weekly Alpha Dispatch
                  </p>
                  <p className="text-xs text-mute">
                    Noise-filtered market education, every week. Free.
                  </p>
                </div>
              </div>
              <div className="mt-5 flex gap-2">
                <input
                  data-testid="newsletter-email-input"
                  type="email"
                  required
                  value={newsEmail}
                  onChange={(e) => setNewsEmail(e.target.value)}
                  placeholder="you@domain.com"
                  className={inputCls}
                />
                <button
                  data-testid="newsletter-submit-button"
                  type="submit"
                  disabled={subscribing}
                  className="flex shrink-0 items-center gap-2 rounded-lg bg-brand px-5 font-mono text-[11px] font-bold uppercase tracking-[0.15em] text-white transition-colors hover:bg-brand-crimson disabled:opacity-60"
                >
                  {subscribing ? <Loader2 size={14} className="animate-spin" /> : "Join"}
                </button>
              </div>
            </form>
          </Reveal>
        </div>

        <Reveal delay={0.12} className="lg:col-span-3">
          <form
            data-testid="contact-form"
            onSubmit={submitContact}
            className="card-trace h-full rounded-xl border border-line bg-panel p-7"
          >
            <p className="font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-brand">
              Consultation Booking
            </p>
            <div className="mt-5 grid gap-4 sm:grid-cols-2">
              <input
                data-testid="contact-form-name-input"
                required
                value={form.name}
                onChange={set("name")}
                placeholder="Full name"
                className={inputCls}
              />
              <input
                data-testid="contact-form-email-input"
                type="email"
                required
                value={form.email}
                onChange={set("email")}
                placeholder="Email address"
                className={inputCls}
              />
              <input
                data-testid="contact-form-phone-input"
                value={form.phone}
                onChange={set("phone")}
                placeholder="Phone / WhatsApp (optional)"
                className={inputCls}
              />
              <select
                data-testid="contact-form-service-select"
                value={form.service}
                onChange={set("service")}
                className={inputCls}
              >
                {SERVICES.map((s) => (
                  <option key={s.id} value={s.title}>
                    {s.title}
                  </option>
                ))}
                <option value="General Inquiry">General Inquiry</option>
              </select>
              <textarea
                data-testid="contact-form-message-input"
                required
                value={form.message}
                onChange={set("message")}
                placeholder="Tell us what you want to learn about markets, technology sectors, or our consultation services..."
                rows={5}
                className={`${inputCls} resize-none sm:col-span-2`}
              />
            </div>
            <button
              data-testid="contact-form-submit-button"
              type="submit"
              disabled={sending}
              className="mt-6 inline-flex items-center gap-2.5 rounded-full bg-brand px-8 py-3.5 font-mono text-xs font-bold uppercase tracking-[0.18em] text-white transition-colors duration-300 hover:bg-brand-crimson disabled:opacity-60"
            >
              {sending ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
              Request Consultation
            </button>
            <p className="mt-4 font-mono text-[10px] leading-relaxed tracking-[0.08em] text-dim">
              Submitting this form requests educational information only. It is
              not an application for financial advice or investment products.
            </p>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
